import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { Button, ModalShell, FormStack, FormField, FormLabel } from '@/components/ui';
import { Input } from '@/components/ui/common/input/Input';
import { useNotificationStore } from '@/store/notificationStore';
import { useSendInquiry } from '@/api/support/useInquiry';
import { validateEmail } from '@/utils/validation/email.validator';

/** 로그인/회원가입 상태에서 자동 채움할 문의자 정보 */
export interface InquiryPrefill {
  name?: string;
  email?: string;
  roleName?: string;
}

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 문의가 시작된 화면 */
  source: 'login' | 'register' | 'settings';
  /** 로그인 상태면 사용자 정보 자동 채움 */
  prefill?: InquiryPrefill;
}

const EMPTY_FORM = {
  name: '',
  email: '',
  subject: '',
  message: '',
};

export const InquiryModal: React.FC<InquiryModalProps> = ({
  isOpen,
  onClose,
  source,
  prefill,
}) => {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useNotificationStore();
  const sendInquiry = useSendInquiry();

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const initializedRef = useRef(false);

  // prefill 값(참조 아닌 원시값)으로 분해 — 부모 리렌더로 인한 불필요한 초기화 방지
  const pfName = prefill?.name ?? '';
  const pfEmail = prefill?.email ?? '';

  // 로그인(설정 화면)에서 연 경우, 이메일은 계정 이메일로 고정하여 수정 불가.
  const emailLocked = source === 'settings' && !!pfEmail;

  // 모달이 열릴 때 1회만 기본 필드 초기화 (사용자 입력을 덮어쓰지 않도록)
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      initializedRef.current = true;
      setForm({ ...EMPTY_FORM, name: pfName, email: pfEmail });
    } else if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, pfName, pfEmail]);

  const setField = (field: keyof typeof form) => (value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.email.trim() || !validateEmail(form.email).isValid) {
      showError(i18nKeys.auth.input_error, i18nKeys.common.inquiry_email_required);
      return;
    }
    if (!form.message.trim()) {
      showError(i18nKeys.auth.input_error, i18nKeys.common.inquiry_message_required);
      return;
    }

    try {
      await sendInquiry.mutateAsync({
        name: form.name.trim() || undefined,
        email: form.email.trim(),
        roleName: prefill?.roleName || undefined,
        subject: form.subject.trim() || undefined,
        message: form.message.trim(),
        source,
        locale: i18n.language?.split('-')[0],
      });
      showSuccess(
        i18nKeys.common.inquiry_success_title,
        i18nKeys.common.inquiry_success_message
      );
      onClose();
    } catch {
      showError(
        i18nKeys.common.inquiry_error_title,
        i18nKeys.common.inquiry_error_message
      );
    }
  };

  const isPending = sendInquiry.isPending;

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.common.inquiry_title)}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            {t(i18nKeys.common.inquiry_cancel)}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isPending}>
            {isPending
              ? t(i18nKeys.common.inquiry_sending)
              : t(i18nKeys.common.inquiry_submit)}
          </Button>
        </>
      }
    >
      <p
        style={{
          marginBottom: '1rem',
          lineHeight: 1.6,
          color: 'inherit',
          whiteSpace: 'pre-line',
        }}
      >
        {t(i18nKeys.common.inquiry_desc)}
      </p>

      <FormStack>
        <FormField>
          <FormLabel>{t(i18nKeys.common.inquiry_email)} *</FormLabel>
          <Input
            type="email"
            value={form.email}
            onChange={setField('email')}
            placeholder={t(i18nKeys.common.inquiry_email_placeholder)}
            hint={
              emailLocked
                ? t(i18nKeys.common.inquiry_email_hint_locked)
                : t(i18nKeys.common.inquiry_email_hint)
            }
            disabled={isPending || emailLocked}
            size="md"
            fullWidth
          />
        </FormField>

        <FormField>
          <FormLabel>{t(i18nKeys.common.inquiry_name)}</FormLabel>
          <Input
            value={form.name}
            onChange={setField('name')}
            placeholder={t(i18nKeys.common.inquiry_name_placeholder)}
            disabled={isPending}
            size="md"
            fullWidth
          />
        </FormField>

        <FormField>
          <FormLabel>{t(i18nKeys.common.inquiry_subject)}</FormLabel>
          <Input
            value={form.subject}
            onChange={setField('subject')}
            placeholder={t(i18nKeys.common.inquiry_subject_placeholder)}
            disabled={isPending}
            size="md"
            fullWidth
          />
        </FormField>

        <FormField>
          <FormLabel>{t(i18nKeys.common.inquiry_message)} *</FormLabel>
          <Input
            value={form.message}
            onChange={setField('message')}
            placeholder={t(i18nKeys.common.inquiry_message_placeholder)}
            disabled={isPending}
            multiline
            rows={6}
            size="md"
            fullWidth
          />
        </FormField>
      </FormStack>
    </ModalShell>
  );
};
